/**
 * AI Onboarding Service
 *
 * Frontend service for AI-powered onboarding suggestions
 * Uses centralized AI service (Gemini via paymentgateway-ab783)
 */

import apiService from './ApiService';
import {
  buildPrompt,
  FALLBACK_TEMPLATES,
  AI_RATE_LIMITS
} from '../constants/aiOnboardingPrompts';

/**
 * Rate limiter to prevent API abuse
 */
class RateLimiter {
  constructor() {
    this.requests = [];
    this.lastRequestTime = 0;
  }

  canMakeRequest() {
    const now = Date.now();
    const oneMinuteAgo = now - 60000;
    const oneHourAgo = now - 3600000;

    // Clean old requests
    this.requests = this.requests.filter(time => time > oneHourAgo);

    // Check rate limits
    const requestsInLastMinute = this.requests.filter(time => time > oneMinuteAgo).length;
    const requestsInLastHour = this.requests.length;
    const timeSinceLastRequest = now - this.lastRequestTime;

    if (requestsInLastMinute >= AI_RATE_LIMITS.maxRequestsPerMinute) {
      throw new Error('Too many requests. Please wait a minute.');
    }

    if (requestsInLastHour >= AI_RATE_LIMITS.maxRequestsPerHour) {
      throw new Error('Hourly limit reached. Please try again later.');
    }

    if (timeSinceLastRequest < AI_RATE_LIMITS.cooldownMs) {
      throw new Error('Please wait a moment between requests.');
    }

    return true;
  }

  recordRequest() {
    const now = Date.now();
    this.requests.push(now);
    this.lastRequestTime = now;
  }
}

const rateLimiter = new RateLimiter();

/**
 * Call AI onboarding suggestion endpoint
 *
 * @param {string} type - Suggestion type (businessName, services, pricing, etc.)
 * @param {object} params - Parameters for the suggestion
 * @returns {Promise<object>} AI suggestion result
 */
async function callAIEndpoint(type, params) {
  // Check rate limit
  rateLimiter.canMakeRequest();

  // Build prompt
  const prompt = buildPrompt(type, params);

  const result = await apiService.post('/ai/onboarding/suggest', {
    type,
    prompt: prompt.combined,
    params
  });

  // Record successful request
  rateLimiter.recordRequest();

  return result;
}

/**
 * Parse AI response safely
 */
function parseAIResponse(response, fallback) {
  try {
    // Try to parse as JSON
    const cleaned = response.trim().replace(/```json\n?/g, '').replace(/```\n?/g, '');
    return JSON.parse(cleaned);
  } catch (error) {
    console.warn('Failed to parse AI response as JSON, using fallback:', error);
    return fallback;
  }
}

/**
 * Suggest business names
 *
 * @param {string} industry - Industry type
 * @param {string} style - Style preference (professional, creative, modern, etc.)
 * @returns {Promise<Array>} Array of { name, reason } objects
 */
export async function suggestBusinessNames(industry, style = 'professional') {
  try {
    const result = await callAIEndpoint('businessName', { industry, style });

    if (result.success && result.suggestion) {
      const parsed = parseAIResponse(
        result.suggestion,
        FALLBACK_TEMPLATES.businessNames[industry] || FALLBACK_TEMPLATES.businessNames.professional
      );

      // Ensure proper format
      if (Array.isArray(parsed)) {
        return parsed.map(item =>
          typeof item === 'string'
            ? { name: item, reason: 'AI suggested' }
            : item
        );
      }
    }

    throw new Error('Invalid AI response');
  } catch (error) {
    console.error('Business name suggestion error:', error);

    // Fallback to templates
    const names = FALLBACK_TEMPLATES.businessNames[industry] ||
      FALLBACK_TEMPLATES.businessNames.professional;

    return names.map(name => ({
      name,
      reason: 'Template suggestion'
    }));
  }
}

/**
 * Suggest services based on industry
 *
 * @param {string} industry - Industry type
 * @param {string} businessSize - 'solo' or 'team'
 * @param {string} location - Optional location for pricing context
 * @returns {Promise<Array>} Array of service objects
 */
export async function suggestServices(industry, businessSize = 'solo', location = '') {
  try {
    const result = await callAIEndpoint('services', { industry, businessSize, location });

    if (result.success && result.suggestion) {
      const parsed = parseAIResponse(
        result.suggestion,
        FALLBACK_TEMPLATES.services[industry] || FALLBACK_TEMPLATES.services.fitness
      );

      if (Array.isArray(parsed)) {
        return parsed.map(service => ({
          name: service.name || 'Service',
          duration: parseInt(service.duration) || 60,
          price: parseFloat(service.price) || 0,
          description: service.description || ''
        }));
      }
    }

    throw new Error('Invalid AI response');
  } catch (error) {
    console.error('Service suggestion error:', error);

    // Fallback to templates
    return FALLBACK_TEMPLATES.services[industry] || FALLBACK_TEMPLATES.services.fitness;
  }
}

/**
 * Suggest pricing for a service
 *
 * @param {string} serviceName - Name of the service
 * @param {string} industry - Industry type
 * @param {string} location - Optional location
 * @returns {Promise<object>} { min, recommended, max, rationale }
 */
export async function suggestPricing(serviceName, industry, location = '') {
  try {
    const result = await callAIEndpoint('pricing', { serviceName, industry, location });

    if (result.success && result.suggestion) {
      const parsed = parseAIResponse(result.suggestion, {
        min: 50,
        recommended: 100,
        max: 200,
        rationale: 'Industry standard pricing'
      });

      return {
        min: parseFloat(parsed.min) || 50,
        recommended: parseFloat(parsed.recommended) || 100,
        max: parseFloat(parsed.max) || 200,
        rationale: parsed.rationale || 'AI-generated pricing suggestion'
      };
    }

    throw new Error('Invalid AI response');
  } catch (error) {
    console.error('Pricing suggestion error:', error);

    // Fallback
    return {
      min: 50,
      recommended: 100,
      max: 200,
      rationale: 'Default pricing range. Adjust based on your market.'
    };
  }
}

/**
 * Suggest optimal availability/working hours
 *
 * @param {string} industry - Industry type
 * @param {string} businessSize - 'solo' or 'team'
 * @returns {Promise<object>} Availability object with days and rationale
 */
export async function suggestAvailability(industry, businessSize = 'solo') {
  try {
    const result = await callAIEndpoint('availability', { industry, businessSize });

    if (result.success && result.suggestion) {
      const parsed = parseAIResponse(result.suggestion, {
        monday: { enabled: true, start: '09:00', end: '17:00' },
        tuesday: { enabled: true, start: '09:00', end: '17:00' },
        wednesday: { enabled: true, start: '09:00', end: '17:00' },
        thursday: { enabled: true, start: '09:00', end: '17:00' },
        friday: { enabled: true, start: '09:00', end: '17:00' },
        saturday: { enabled: false, start: '10:00', end: '14:00' },
        sunday: { enabled: false, start: '10:00', end: '14:00' },
        rationale: 'Standard business hours'
      });

      return parsed;
    }

    throw new Error('Invalid AI response');
  } catch (error) {
    console.error('Availability suggestion error:', error);

    // Fallback
    return {
      monday: { enabled: true, start: '09:00', end: '17:00' },
      tuesday: { enabled: true, start: '09:00', end: '17:00' },
      wednesday: { enabled: true, start: '09:00', end: '17:00' },
      thursday: { enabled: true, start: '09:00', end: '17:00' },
      friday: { enabled: true, start: '09:00', end: '17:00' },
      saturday: { enabled: false, start: '10:00', end: '14:00' },
      sunday: { enabled: false, start: '10:00', end: '14:00' },
      rationale: 'Standard business hours. Adjust based on customer demand.'
    };
  }
}

/**
 * Generate service description
 *
 * @param {string} serviceName - Name of the service
 * @param {string} industry - Industry type
 * @param {number} duration - Service duration in minutes
 * @param {number} price - Service price
 * @returns {Promise<string>} Generated description
 */
export async function generateServiceDescription(serviceName, industry, duration, price) {
  try {
    const result = await callAIEndpoint('description', { serviceName, industry, duration, price });

    if (result.success && result.suggestion) {
      return result.suggestion.trim();
    }

    throw new Error('Invalid AI response');
  } catch (error) {
    console.error('Description generation error:', error);

    // Fallback
    return `Professional ${serviceName} service. Duration: ${duration} minutes. Price: $${price}.`;
  }
}

/**
 * Get quick help answer
 *
 * @param {string} question - User's question
 * @param {string} currentStep - Current onboarding step
 * @returns {Promise<string>} AI answer
 */
export async function getQuickHelp(question, currentStep = '') {
  try {
    const result = await callAIEndpoint('quickHelp', { question, currentStep });

    if (result.success && result.suggestion) {
      return result.suggestion.trim();
    }

    throw new Error('Invalid AI response');
  } catch (error) {
    console.error('Quick help error:', error);

    // Fallback
    return "I'm having trouble answering that right now. Please check our documentation or contact support.";
  }
}

export default {
  suggestBusinessNames,
  suggestServices,
  suggestPricing,
  suggestAvailability,
  generateServiceDescription,
  getQuickHelp
};
